/**
 * SVG plan symbols (chair, bath, shower…) as editable spec shapes.
 *
 * The built-in SVG glyphs are parsed into unit-box segments
 * (glyphs/svgGlyphs.ts `getUnitGlyph`) and stretched to the footprint. Here
 * the same unit segments are scaled to a concrete width × depth — the
 * symbol's size in the spec's authored frame, centred on the origin — and
 * chained into polylines (joined where consecutive segments meet), one shape per outline. A stretch group then gives
 * exactly the built-in's footprint stretch; a fixed group placed with an
 * `offset` gives a symbol of constant size (the chairs around a table).
 */

import type { ObjectPolylineShape, Vec2 } from 'shared-types';
import { getUnitGlyph } from '../../glyphs/svgGlyphs';

/** Coordinates are rounded to 0.01 mm so the spec stays readable. */
const round = (v: number): number => Math.round(v * 1e5) / 1e5;

const samePoint = (a: Vec2, b: Vec2): boolean => Math.abs(a[0] - b[0]) < 1e-6 && Math.abs(a[1] - b[1]) < 1e-6;

/**
 * Chain unit segments (`[ax,ay, bx,by, …]`, scaled) into polylines, joining
 * consecutive segments that share an endpoint. (Same as fromBuiltin's
 * `segmentsToPolylines`; kept here because fromBuiltin imports the built-in
 * specs, which are built from this module at load time.)
 */
function chain(unit: Float32Array, width: number, depth: number): Vec2[][] {
  const lines: Vec2[][] = [];
  let current: Vec2[] | null = null;
  for (let i = 0; i + 3 < unit.length; i += 4) {
    const a: Vec2 = [unit[i] * width, unit[i + 1] * depth];
    const b: Vec2 = [unit[i + 2] * width, unit[i + 3] * depth];
    if (current && samePoint(current[current.length - 1], a)) {
      current.push(b);
    } else {
      current = [a, b];
      lines.push(current);
    }
  }
  return lines;
}

/**
 * The SVG `source` (cached under `assetKey`, like the built-ins) as polyline
 * shapes spanning `width` × `depth` around the origin, ids `${idPrefix}1…`,
 * names `${name} part 1…`.
 * Outlines that return to their start become closed polylines.
 */
export function svgSymbolShapes(assetKey: string, source: string, width: number, depth: number, idPrefix: string, name: string): ObjectPolylineShape[] {
  return chain(getUnitGlyph(assetKey, source), width, depth).map((line, i): ObjectPolylineShape => {
    const points = line.map(([u, n]): Vec2 => [round(u), round(n)]);
    const closed = points.length > 3 && samePoint(points[0], points[points.length - 1]);
    return { id: `${idPrefix}${i + 1}`, name: `${name} part ${i + 1}`, kind: 'polyline', points: closed ? points.slice(0, -1) : points, closed };
  });
}
